import styled from 'styled-components';

import { ds, dsRadius } from '@/config/colors';
import { media } from '@/utils';

import { TOP_BAR_HEIGHT, TOP_BAR_HEIGHT_COMPACT } from '../Layout/Layout.styled';

/*
 * Panel czatu AI w stylu hotelspot-audit („Zapytaj AI o raport"): biała karta z cienką
 * ramką, nagłówek z ikonami, pusty obszar rozmowy i kompozytor na dole z podpowiedziami.
 * Sam wygląd; logika rozmowy przychodzi osobno przez propsy/handlery.
 */

/** Szerokość szuflady; strony odsuwają o nią treść, gdy czat jest otwarty. */
export const AI_CHAT_WIDTH = 420;
/** Odstęp między treścią odsuniętą przez czat a krawędzią szuflady. */
export const AI_CHAT_GAP = 24;

/* Szuflada przy prawej krawędzi, od samej góry treści (pod TopBarem) do dołu ekranu,
   jak w audycie. Nie karta w siatce strony, więc nie przewija się z treścią. */
export const Panel = styled.aside`
  position: fixed;
  top: ${TOP_BAR_HEIGHT}px;
  right: 0;
  bottom: 0;
  z-index: 45;
  display: flex;
  flex-direction: column;
  width: min(${AI_CHAT_WIDTH}px, 100vw);
  background: ${ds.bgSurface};
  border-left: 1px solid ${ds.borderDefault};
  box-sizing: border-box;
  overflow: hidden;

  ${media.tabletDown} {
    top: ${TOP_BAR_HEIGHT_COMPACT}px;
  }
`;

export const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 20px 0;
`;

export const Title = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${ds.textPrimary};
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: ${dsRadius['8']};
  background: transparent;
  color: ${ds.textMuted};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${ds.bgMuted};
    color: ${ds.textPrimary};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

export const Description = styled.p`
  margin: 12px 0 0;
  padding: 0 20px;
  font-size: 14px;
  line-height: 1.5;
  color: ${ds.textSecondary};
`;

export const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
`;

export const Composer = styled.div`
  margin: 0 16px;
  padding: 10px;
  border: 1px solid ${ds.borderDefault};
  border-radius: ${dsRadius['16']};
  background: ${ds.bgSurface};
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
`;

export const Suggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
`;

export const Suggestion = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 8px 12px;
  border: 1px solid ${ds.borderDefault};
  border-radius: ${dsRadius['12']};
  background: ${ds.bgSurface};
  color: ${ds.textPrimary};
  font-size: 13px;
  line-height: 1.3;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  svg {
    flex-shrink: 0;
    color: ${ds.textMuted};
  }

  &:hover {
    background: ${ds.bgMuted};
    border-color: ${ds.borderStrong};
  }
`;

/** Rośnie z treścią do 4 linii (wysokość liczona w komponencie), potem przewija. */
export const Input = styled.textarea`
  display: block;
  width: 100%;
  min-height: 33px;
  padding: 8px 8px 4px;
  overflow-y: hidden;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  font: inherit;
  font-size: 14px;
  line-height: 1.5;
  color: ${ds.textPrimary};

  &::placeholder {
    color: ${ds.textMuted};
  }
`;

export const ComposerFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 4px 2px 8px;
`;

export const AttachButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 4px;
  border: none;
  background: transparent;
  color: ${ds.textSecondary};
  font-size: 13px;
  cursor: pointer;
  border-radius: ${dsRadius['8']};

  &:hover {
    color: ${ds.textPrimary};
  }
`;

export const TokenCount = styled.span`
  margin-left: auto;
  font-size: 12px;
  color: ${ds.textMuted};
  white-space: nowrap;
`;

export const SendButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 9999px;
  background: ${ds.accentInk};
  color: #ffffff;
  cursor: pointer;
  transition: background 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    background: ${ds.accentInkHover};
  }

  &:disabled {
    background: ${ds.bgMuted};
    color: ${ds.textMuted};
    cursor: default;
  }
`;

export const Disclaimer = styled.p`
  margin: 0;
  padding: 10px 20px 16px;
  font-size: 12px;
  line-height: 1.45;
  color: ${ds.textMuted};
`;

/** Odstęp pod kompozytorem, gdy nie ma disclaimera. */
export const BottomSpace = styled.div`
  height: 16px;
  flex-shrink: 0;
`;

/* ── Rozmowa ───────────────────────────────────────────────────────────────── */

export const MessageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const Bubble = styled.div<{ $role: 'user' | 'assistant' }>`
  max-width: 92%;
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  padding: 10px 14px;
  border-radius: ${dsRadius['16']};
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  ${({ $role }) => ($role === 'user'
    ? `background: ${ds.accentInk}; color: #ffffff; border-bottom-right-radius: 4px;`
    : `background: ${ds.bgMuted}; color: ${ds.textPrimary}; border-bottom-left-radius: 4px;`)}

  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12.5px;
    padding: 1px 5px;
    border-radius: 4px;
    background: rgba(15, 23, 42, 0.08);
  }
  ul { margin: 6px 0 0; padding-left: 18px; }
  li { margin: 2px 0; }
  strong { font-weight: 600; }
`;

export const BubbleMeta = styled.div<{ $role: 'user' | 'assistant' }>`
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  margin-top: -6px;
  font-size: 11.5px;
  color: ${ds.textMuted};
  display: flex;
  gap: 6px;
  align-items: center;
`;

export const ErrorNote = styled.div`
  align-self: flex-end;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: -6px;
  font-size: 12px;
  color: #b91c1c;
`;

export const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border: 1px solid ${ds.borderDefault};
  border-radius: 999px;
  background: ${ds.bgSurface};
  color: ${ds.textPrimary};
  font-size: 12px;
  cursor: pointer;
  &:hover { background: ${ds.bgMuted}; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

export const Typing = styled.div`
  align-self: flex-start;
  display: inline-flex;
  gap: 4px;
  padding: 12px 14px;
  border-radius: ${dsRadius['16']};
  background: ${ds.bgMuted};

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${ds.textMuted};
    animation: hsTyping 1.2s ease-in-out infinite;
  }
  span:nth-child(2) { animation-delay: 0.15s; }
  span:nth-child(3) { animation-delay: 0.3s; }

  @keyframes hsTyping {
    0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
    40% { transform: translateY(-3px); opacity: 1; }
  }
`;

/* ── Karta propozycji ──────────────────────────────────────────────────────── */

/* Karta na szerokość dymka odpowiedzi (92 %, do lewej), rzędy rozdzielone liniami. */
export const ProposalCard = styled.div`
  align-self: flex-start;
  width: 92%;
  max-width: 92%;
  border: 1px solid ${ds.borderDefault};
  border-radius: ${dsRadius['12']};
  background: ${ds.bgSurface};
  overflow: hidden;
`;

export const ProposalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 600;
  color: ${ds.textPrimary};
  border-bottom: 1px solid ${ds.borderDefault};
  background: ${ds.bgMuted};
`;

export const ProposalRows = styled.dl`
  margin: 0;
  padding: 0;
`;

export const ProposalRow = styled.div<{ $invalid?: boolean }>`
  display: grid;
  grid-template-columns: 104px minmax(0, 1fr);
  gap: 10px;
  padding: 10px 14px;
  font-size: 13px;
  line-height: 1.45;
  opacity: ${({ $invalid }) => ($invalid ? 0.55 : 1)};
  border-bottom: 1px solid ${ds.borderDefault};

  &:last-child {
    border-bottom: none;
  }

  dt {
    color: ${ds.textPrimary};
    font-weight: 600;
  }
  dd {
    margin: 0;
    color: ${ds.textPrimary};
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

export const ProposalNote = styled.div<{ $tone?: 'warn' | 'muted' }>`
  padding: 6px 14px 10px;
  font-size: 12px;
  line-height: 1.45;
  color: ${({ $tone }) => ($tone === 'warn' ? '#b45309' : ds.textMuted)};
`;

/* Blok decyzji POD kartą (osobny element, bez tła): pytanie + Zastosuj / Zmień. */
export const ProposalActions = styled.div`
  align-self: flex-start;
  width: 92%;
  max-width: 92%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: -4px;
`;

/** Pytanie o zgodę nad przyciskami („Zastosować te wartości w formularzu?”). */
export const ProposalQuestion = styled.div<{ $tone?: 'success' }>`
  flex: 1 0 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $tone }) => ($tone === 'success' ? '#15803d' : ds.textPrimary)};
`;

const pill = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 14px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
  &:disabled { opacity: 0.5; cursor: default; }
`;

export const ApplyButton = styled.button`
  ${pill}
  flex: 1;
  background: ${ds.accentInk};
  color: #ffffff;
  border: 1px solid ${ds.accentInk};
  &:hover:not(:disabled) { background: ${ds.accentInkHover}; }
`;

export const GhostButton = styled.button`
  ${pill}
  background: ${ds.bgSurface};
  color: ${ds.textPrimary};
  border: 1px solid ${ds.borderDefault};
  &:hover:not(:disabled) { background: ${ds.bgMuted}; }
`;

/* ── Okładka od AI ─────────────────────────────────────────────────────────── */

/** Podgląd okładki w proporcjach pola (`4/3`, `1/1`, `3/2`), żeby było widać kadr przed „Użyj”. */
export const CoverPreview = styled.div<{ $ratio: string; $dim?: boolean }>`
  position: relative;
  aspect-ratio: ${({ $ratio }) => $ratio};
  background: ${ds.bgMuted};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  color: ${ds.textMuted};
  opacity: ${({ $dim }) => ($dim ? 0.45 : 1)};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

/** Warstwa nad podglądem w trakcie generowania albo zapisu (poprzedni obraz zostaje pod spodem). */
export const CoverBusy = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: ${ds.textPrimary};
  background: color-mix(in srgb, ${ds.bgSurface} 72%, transparent);
  backdrop-filter: blur(2px);
`;

/** Akcje okładki w karcie, pod obrazem: „Zastosuj” na całą szerokość, niżej akcje dodatkowe. */
export const CoverFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px 14px;
  border-top: 1px solid ${ds.borderDefault};
`;

export const CoverActionsRow = styled.div`
  display: flex;
  gap: 8px;

  & > * { flex: 1; }
`;

export const CoverChangesForm = styled.form`
  display: contents;
`;

export const CoverChangesLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${ds.textPrimary};
`;

export const CoverChangesInput = styled.textarea`
  display: block;
  width: 100%;
  min-height: 72px;
  padding: 8px 10px;
  border: 1px solid ${ds.borderDefault};
  border-radius: ${dsRadius['12']};
  outline: none;
  resize: vertical;
  background: ${ds.bgSurface};
  font: inherit;
  font-size: 13px;
  line-height: 1.5;
  color: ${ds.textPrimary};

  &:focus { border-color: ${ds.borderStrong}; }
  &::placeholder { color: ${ds.textMuted}; }
`;

export const CoverHint = styled.div<{ $tone?: 'success' }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 1.45;
  color: ${({ $tone }) => ($tone === 'success' ? '#15803d' : ds.textMuted)};
`;

/* ── Załączniki w kompozytorze ─────────────────────────────────────────────── */

export const AttachmentChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 8px 6px;
`;

export const AttachmentChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 4px 8px 4px 10px;
  border-radius: 9999px;
  background: ${ds.bgMuted};
  font-size: 12px;
  color: ${ds.textPrimary};

  span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 180px; }
  button {
    display: inline-flex;
    border: none;
    background: transparent;
    color: ${ds.textMuted};
    cursor: pointer;
    padding: 2px;
    &:hover { color: ${ds.textPrimary}; }
  }
`;

/* ── Historia ──────────────────────────────────────────────────────────────── */

export const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 0;
`;

export const HistoryItem = styled.button<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ $active }) => ($active ? ds.borderStrong : 'transparent')};
  border-radius: ${dsRadius['12']};
  background: ${({ $active }) => ($active ? ds.bgMuted : 'transparent')};
  text-align: left;
  cursor: pointer;
  font: inherit;

  &:hover { background: ${ds.bgMuted}; }

  strong {
    font-size: 13.5px;
    font-weight: 500;
    color: ${ds.textPrimary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
  small { font-size: 12px; color: ${ds.textMuted}; }
`;

export const HistoryEmpty = styled.p`
  margin: 0;
  padding: 12px 0;
  text-align: left;
  font-size: 13px;
  color: ${ds.textMuted};
`;

export const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${ds.textMuted};
`;

/** Znacznik „Wpisano” w nagłówku karty: biały tekst na zielonym tle, bez ikony. */
export const AppliedBadge = styled.span`
  margin-left: auto;
  padding: 2px 10px;
  border-radius: 9999px;
  background: #15803d;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
`;

/** Wiersz loadera przy pobieraniu wątku z historii. */
export const LoaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 24px 0;
  font-size: 13px;
  color: ${ds.textSecondary};
`;

/** Logo Gemini w nagłówku: model, który odpowiada, zamiast ogólnej ikony „AI". */
export const HeaderLogo = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
`;
